export const extractAccessCode = (accessCodeData) => {
  if (!accessCodeData) {
    return null;
  }
  if (Array.isArray(accessCodeData) && accessCodeData.length > 0) {
    return accessCodeData[0]?.accesscode || null;
  }
  else if (typeof accessCodeData === 'object' && accessCodeData !== null && accessCodeData.accesscode) {
    return accessCodeData.accesscode;
  }
  else if (typeof accessCodeData === 'string') {
    const trimmed = accessCodeData.trim();
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        return extractAccessCode(parsed);
      } catch (e) {
        return trimmed || null;
      }
    }
    return trimmed || null;
  }

  return null;
};

export const getSessionType = (session) => {
  const appointmentType = String(session?.appointmentType || "").toLowerCase().trim();
  const providerType = String(
    session?.health_specialist?.providerType || session?.providerType || ""
  )
    .toLowerCase()
    .trim();

  if (providerType === "care manager") {
    return "Initial Assessment";
  }

  if (
    !appointmentType ||
    appointmentType === "initial" ||
    appointmentType.includes("initial") ||
    appointmentType.includes("assessment")
  ) {
    return "Initial Assessment";
  }

  return "Therapy";
};

export const getAppointmentStatusLabel = (session) => {
  const appointmentStatus = session?.status || "Scheduled";

  if (appointmentStatus === "Upcoming") {
    return "Scheduled";
  }

  const statusMap = {
    Scheduled: "Scheduled",
    Completed: "Completed",
    Cancelled: "User Cancel",
    canceled_by_user: "User Cancel",
    "Canceled by Provider": "Provider Cancel",
    "Canceled by Super Admin": "Admin Cancel",
    "System Cancel": "Admin Cancel",
    late_cancelled: "Late Cancel",
    "No Show": "No Show",
    Rescheduled: "Rescheduled",
  };

  return statusMap[appointmentStatus] || appointmentStatus;
};

export const getPaymentMethod = (session, paymentDataBySession) => {
  const sessionId = session._id || session.id;
  const paymentData = paymentDataBySession[sessionId];

  const paymentMethod = paymentData?.paymentMethod ||
    session?.paymentData?.paymentMethod ||
    session?.user?.paymentMethod ||
    session?.paymentMethod;

  if (!paymentMethod || (typeof paymentMethod === 'string' && paymentMethod.trim() === '')) {
    return "-";
  }
  return paymentMethod;
};

export const getVerificationStatus = (session, paymentDataBySession, showBillingDetails = false) => {
  const sessionId = session._id || session.id;
  const paymentData = paymentDataBySession[sessionId];
  const paymentMethod = getPaymentMethod(session, paymentDataBySession);

  if (!paymentMethod || paymentMethod === "-" || paymentMethod.trim() === "") {
    return "Invalid";
  }
  const paymentMethodLower = paymentMethod.toLowerCase();

  if (paymentMethodLower === "insurance") {
    // Prefer explicit verification status from payment when present.
    const explicitStatus = String(
      paymentData?.insuranceVerificationStatus || "",
    )
      .toLowerCase()
      .trim();
    if (explicitStatus === "verified") {
      return "Verified";
    }
    if (explicitStatus === "invalid") {
      return "Invalid";
    }
    if (explicitStatus === "pending") {
      return "Pending";
    }

    const insuranceActive =
      paymentData?.insuranceActive === true ||
      paymentData?.insuranceEligibilityVerified === true;
    const missingDocuments = paymentData?.missingDocuments === true;
    const policyInactive =
      paymentData?.policyInactive === true ||
      paymentData?.insuranceExpired === true;

    if (insuranceActive && !missingDocuments && !policyInactive) {
      return "Verified";
    }
    if (policyInactive) {
      return "Invalid";
    }
    return "Pending";
  }

  if (paymentMethodLower === "self-pay" || paymentMethodLower === "cash") {
    const cardOnFile = paymentData?.cardOnFileVerified === true;
    const cardNotExpired = paymentData?.cardExpired !== true;
    const preauthorizationSuccessful = paymentData?.cardAuthorized === true;
    const cardTokenValid = paymentData?.cardTokenInvalid !== true;

    const cardExpired = paymentData?.cardExpired === true;
    const cardRemoved = paymentData?.cardRemoved === true;
    const cardTokenInvalid = paymentData?.cardTokenInvalid === true;
    const preauthorizationFailed = paymentData?.cardAuthorized === false;
    const paymentIntentCanceled = paymentData?.paymentIntentCanceled === true;
    const paymentMethodUpdateRequired = paymentData?.paymentMethodUpdateRequired === true;
    const sessionCannotProceed = paymentData?.sessionCannotProceed === true;

    if (cardOnFile && cardNotExpired && preauthorizationSuccessful && cardTokenValid && !paymentIntentCanceled) {
      return "Verified";
    }
    else if (cardExpired || cardRemoved || cardTokenInvalid || preauthorizationFailed || paymentIntentCanceled || paymentMethodUpdateRequired || sessionCannotProceed) {
      return "Invalid";
    }
    else {
      return "Pending";
    }
  }

  if (paymentMethodLower === "access code") {
    const accessCodeExists = paymentData?.accessCodeExists === true;
    const codeIsActive = paymentData?.accessCodeActive === true;
    const businessIsActive = paymentData?.businessIsActive === true;
    const sessionAuthorized = paymentData?.sessionAuthorized === true;

    const codeNotFound = paymentData?.accessCodeNotFound === true;
    const codeExpired = paymentData?.accessCodeExpired === true;
    const businessDeactivated = paymentData?.businessIsActive === false;
    const needsReview = paymentData?.accessCodeNeedsReview === true;

    if (accessCodeExists && codeIsActive && businessIsActive && sessionAuthorized) {
      return "Verified";
    }
    if (needsReview || (!accessCodeExists && !codeNotFound && !codeExpired && paymentData)) {
      return "Pending";
    }
    if (codeNotFound || codeExpired || businessDeactivated || !codeIsActive || !sessionAuthorized) {
      return "Invalid";
    }
    return "Pending";
  }

  return "Invalid";
};

export const getDatePresetRange = (preset) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const endOfDay = (date) => {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
  };

  switch (preset) {
    case "Today": {
      return { start: today, end: endOfDay(today) };
    }
    case "Tomorrow": {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return { start: tomorrow, end: endOfDay(tomorrow) };
    }
    case "This Week": {
      const start = new Date(today);
      const day = start.getDay();
      const diffToMonday = day === 0 ? -6 : 1 - day;
      start.setDate(start.getDate() + diffToMonday);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { start, end: endOfDay(end) };
    }
    case "Next 7 Days": {
      const end = new Date(today);
      end.setDate(today.getDate() + 6);
      return { start: today, end: endOfDay(end) };
    }
    default:
      return null;
  }
};

export const getFilteredSessions = (
  sessions,
  debouncedSearch,
  statusFilter,
  paymentMethodFilter,
  paymentDataBySession,
  filterUpcoming = false,
  pastAppointment = false,
  sessionTypeFilter = "All",
  appointmentStatusFilter = "All"
) => {
  let filtered = sessions;
  if (filterUpcoming) {
    const now = new Date();
    filtered = filtered.filter((session) => {
      const appointmentTime = session.appointmenttime || session.appointmentdate || session.date;
      if (!appointmentTime) return false;
      const sessionDate = new Date(appointmentTime);
      return sessionDate > now;
    });
  }

  if (pastAppointment) {
    const now = new Date();
    filtered = filtered.filter((session) => {
      const appointmentTime =
        session.appointmenttime || session.appointmentdate || session.date;
      if (!appointmentTime) return false;
      const sessionDate = new Date(appointmentTime);
      return sessionDate < now;
    });
  }

  if (debouncedSearch && debouncedSearch.trim() !== "") {
    const searchTerm = debouncedSearch.trim().toLowerCase();
    filtered = filtered.filter((session) => {
      const clientName = `${session?.user?.firstname || ""} ${session?.user?.lastname || ""}`.toLowerCase();
      const providerName = `${session?.health_specialist?.firstname || ""} ${session?.health_specialist?.lastname || ""}`.toLowerCase();
      const sessionId = (session.appointment_id || session._id || "").toLowerCase();

      return (
        clientName.includes(searchTerm) ||
        providerName.includes(searchTerm) ||
        sessionId.includes(searchTerm)
      );
    });
  }

  if (sessionTypeFilter !== "All") {
    filtered = filtered.filter(
      (session) => getSessionType(session) === sessionTypeFilter
    );
  }

  if (appointmentStatusFilter !== "All") {
    filtered = filtered.filter(
      (session) => getAppointmentStatusLabel(session) === appointmentStatusFilter
    );
  }

  if (statusFilter !== "All") {
    filtered = filtered.filter((session) => {
      const verificationStatus = getVerificationStatus(session, paymentDataBySession);
      return verificationStatus === statusFilter;
    });
  }

  if (paymentMethodFilter !== "All") {
    filtered = filtered.filter((session) => {
      const paymentMethod = getPaymentMethod(session, paymentDataBySession);
      if (!paymentMethod || paymentMethod === "-" || paymentMethod.trim() === "") {
        return false;
      }

      const paymentMethodLower = paymentMethod.toLowerCase().trim();

      if (paymentMethodFilter === "Insurance") {
        return paymentMethodLower === "insurance";
      } else if (paymentMethodFilter === "Self-Pay") {
        return paymentMethodLower === "self-pay" || paymentMethodLower === "cash";
      } else if (paymentMethodFilter === "Access Code") {
        return paymentMethodLower === "access code";
      }
      return false;
    });
  }

  return filtered;
};

export const getSortedSessions = (sessionsToSort, sortConfig, paymentDataBySession) => {
  const sorted = [...sessionsToSort];

  sorted.sort((a, b) => {
    let aValue, bValue;

    switch (sortConfig.key) {
      case "date":
        aValue = new Date(a.appointmenttime || a.appointmentdate || a.date);
        bValue = new Date(b.appointmenttime || b.appointmentdate || b.date);
        break;
      case "provider":
        aValue = `${a?.health_specialist?.firstname || ""} ${a?.health_specialist?.lastname || ""}`.toLowerCase();
        bValue = `${b?.health_specialist?.firstname || ""} ${b?.health_specialist?.lastname || ""}`.toLowerCase();
        break;
      case "status":
        aValue = getVerificationStatus(a, paymentDataBySession);
        bValue = getVerificationStatus(b, paymentDataBySession);
        break;
      case "paymentMethod":
        aValue = getPaymentMethod(a, paymentDataBySession).toLowerCase();
        bValue = getPaymentMethod(b, paymentDataBySession).toLowerCase();
        break;
      case "sessionType":
        aValue = getSessionType(a).toLowerCase();
        bValue = getSessionType(b).toLowerCase();
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  return sorted;
};
